import { OutvioCode } from 'app-types';
import { Descendant } from 'slate';

export enum ReturnMethods {
  ORIGINAL = 'original',
  STORE_CREDIT = 'storeCredit',
}

export enum BannerModes {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  MOBILE = 'mobile',
}

export enum ButtonVariant {
  PRIMARY = 'primary',
  TRANSPARENT = 'transparent',
}

export enum BannerTypes {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

export { OutvioCode };

export type DisplayStatusCodes =
  | OutvioCode.RECEIVED
  | OutvioCode.PROCESSING
  | OutvioCode.CREATED
  | OutvioCode.PICKED_UP
  | OutvioCode.IN_TRANSIT
  | OutvioCode.DELIVERED_TO_PICKUP_POINT
  | OutvioCode.DELIVERED_TO_SHOP
  | OutvioCode.OUT_FOR_DELIVERY
  | OutvioCode.DELIVERED
  | OutvioCode.HAND_OVER;

/**
 * Uncivilicised way of dealing with run-time type checking
 */
export const displayStatus: DisplayStatusCodes[] = [
  OutvioCode.RECEIVED,
  OutvioCode.PROCESSING,
  OutvioCode.CREATED,
  OutvioCode.PICKED_UP,
  OutvioCode.IN_TRANSIT,
  OutvioCode.OUT_FOR_DELIVERY,
  OutvioCode.DELIVERED_TO_PICKUP_POINT,
  OutvioCode.DELIVERED_TO_SHOP,
  OutvioCode.DELIVERED,
  OutvioCode.HAND_OVER,
];

export type IncidentStatusSteps =
  | OutvioCode.BAD_ADDRESS // IT, OFD
  | OutvioCode.CANCELLED // any
  | OutvioCode.DAMAGED // IT OFD
  | OutvioCode.DESTROYED // IT OFD
  | OutvioCode.DELAYED // IT
  | OutvioCode.MISSING_DOCUMENTATION // IT OFD
  | OutvioCode.IMPORTANT_INCIDENTS // any
  | OutvioCode.LOST // IT OFD
  | OutvioCode.NOT_AT_HOME // OFD
  | OutvioCode.OTHER_INCIDENTS // all
  | OutvioCode.PARTIALLY_DELIVERED // OFD
  | OutvioCode.REFUSED // OFD
  | OutvioCode.RETURN_TO_ORIGIN // OFD
  | OutvioCode.STOLEN; // IT OFD

export const incidentStatusSteps = [
  OutvioCode.BAD_ADDRESS,
  OutvioCode.CANCELLED,
  OutvioCode.DAMAGED,
  OutvioCode.DESTROYED,
  OutvioCode.DELAYED,
  OutvioCode.MISSING_DOCUMENTATION,
  OutvioCode.IMPORTANT_INCIDENTS,
  OutvioCode.LOST,
  OutvioCode.NOT_AT_HOME,
  OutvioCode.OTHER_INCIDENTS,
  OutvioCode.PARTIALLY_DELIVERED,
  OutvioCode.REFUSED,
  OutvioCode.RETURN_TO_ORIGIN,
  OutvioCode.STOLEN,
];

export interface IBanner {
  imgSrc: string;
  alt: string;
  mode: BannerModes;
}

export interface IMapColors {
  mapBase: string; // Same
  mapWater: string; // New | Serialised
  mapPath: string; // Same
  mapDivisor: string; // New | Serialised
  mapText: string; // Same
  mapPin: string; // Same
}

export interface IColors extends IMapColors {
  primary: string; // New | previously btnBg
  primaryAlt: string; // New | Serialised
  secondary: string; // New
  border: string; // New | Serialised
  inputBg: string; // New (same as border) | Serialised
  alertColor: string; // Same
  alertAlt: string; // New
  primaryText: string; // Same
  btnText: string; // Same
  primaryBg: string; // Same
  secondaryBg: string; // Same
  header: string;
  btnBg: string; // Same
}

export interface IBanners {
  [BannerTypes.MOBILE]?: IBanner;
  [BannerTypes.DESKTOP]?: IBanner;
  url?: string;
}

export interface IOrderStatusInfo {
  title: string;
  moreInfoText: string;
  manageIncidentText: string;
  lastUpdate: string;
  titleOrder: string;
  estimatedDeliveryDate: string;
  seeOderDetails: string;
}

export interface IDeliveryInfo {
  title: string;
  returnTitle: string;
  changeInformationText: string;
  thanksTitle: string;
  thanksText: string;
  thanksButton: string;
  confirmChangeButton: string;
  checkboxText: Descendant[];
}

export interface IOrderInfo {
  orderTotalText: string;
}

export interface IQuestions {
  title: string;
  questions?: IQuestion[];
}

export interface IQuestion {
  order: number;
  title: string;
  answer: Descendant[];
}

export interface IInfoStep {
  title: string;
  message: string;
  checkbox: Descendant[];
  confirmDialogButton?: string;
  cancelDialogButton?: string;
  cancelOrderButton?: string;
}

export interface IReturnMethod {
  text: string;
}

export interface IReturnMethods {
  [ReturnMethods.STORE_CREDIT]: IReturnMethod;
  [ReturnMethods.ORIGINAL]: IReturnMethod;
}

export interface IReturnStep {
  title: string;
  refundText: string;
  methods: IReturnMethods;
  button: string;
  checkbox: Descendant[];
}

export interface ICancelledStep {
  title: string;
  comment: string;
  button: string;
}

export interface ICancelOrder {
  infoStep: IInfoStep;
  returnStep: IReturnStep;
  cancelledStep: ICancelledStep;
}

export interface IOrderStatusSteps {
  [OutvioCode.RECEIVED]: string;
  [OutvioCode.PROCESSING]: string;
  [OutvioCode.CREATED]: string;
  [OutvioCode.PICKED_UP]: string;
  [OutvioCode.IN_TRANSIT]: string;
  [OutvioCode.OUT_FOR_DELIVERY]: string;
  [OutvioCode.DELIVERED_TO_PICKUP_POINT]: string;
  [OutvioCode.DELIVERED_TO_SHOP]: string;
  [OutvioCode.DELIVERED]: string;
  [OutvioCode.HAND_OVER]: string;
  [OutvioCode.DELETED_SHIPMENT]: string;
}

export interface IRating {
  body: string;
  askLater: string;
  rateButton: string;
  thanks: string;
}

export interface ISuggestedProducts {
  buttonShopNow: string;
  titleProductsForYou: string;
}

export interface ITracking {
  titleTracking: string;
  titleCourier: string;
  titleTrackingNumber: string;
  buttonPackage: string;
  titleMultiShipments: string;
  buttonShipment: string;
}

export interface ILoginPAgeInfo {
  titleTrackingPortal: string;
  inputInsertIdOrTrackingNumber: string;
  inputInsertDestinationPostcode: string;
  buttonTrackOrder: string;
}
export interface IRegionalSettings {
  language: ILanguage;
  banners?: IBanners;
  orderStatusInfo: IOrderStatusInfo;
  cancelOrder: ICancelOrder;
  deliveryInfo: IDeliveryInfo;
  orderInfo: IOrderInfo;
  questions: IQuestions;
  shipmentNotFoundText: string;
  orderStatusSteps: IOrderStatusSteps;
  rating: IRating;
  suggestedProducts: ISuggestedProducts;
  tracking: ITracking;
  loginPageInfo: ILoginPAgeInfo;
}

export interface ICustomUrls {
  tracking?: string;
  returns?: string;
}

export interface ILanguage {
  isDefault: boolean;
  /**
   * Example et-EE for Estonian
   */
  ISOCode: string;
  /**
   * Example et for Estonian
   */
  name: string;
}

export interface IGeneral {
  colors: IColors;
  customUrls: ICustomUrls;
  favicon?: string | null;
  isBannerDisplay: boolean;
  isDeliveryInfoChangeable: boolean;
  isOrderCancellable?: boolean;
  logo?: string | null;
  useCustomMapMarker: boolean;
  useCustomMapColors: boolean;
  customMapMarker?: string;
  useRateOrder: boolean;
  enableMapOnTrackingPage?: boolean;
  mapFeatureExpireAtTheEndOfMonth?: boolean;
  allowOrderCancel?: CancelOrderType;
  autoRefundOnCancel?: boolean;
  trackingDesktopBg?: string;
  hideProductPrices?: boolean;
}

export interface IPortalSettings extends IGeneral {
  regionalSettings: IRegionalSettings[];
}

export interface ISingleRegionPortalSettings extends IGeneral {
  regionalSettings: IRegionalSettings;
}

export enum CancelOrderType {
  'not_shipped' = 'not_shipped',
  '24h' = '24h',
}
